
import React, { useState, useEffect } from "react";
import { Search, Calendar, ShoppingCart, ChevronDown, ChevronUp, Check, AlertCircle, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { format } from "date-fns";
import { Transaction } from "@/api/entities";
import { motion, AnimatePresence } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";

export default function TransactionsSection({ 
  selectedCardholder, 
  selectedCard,
  selectedTransactions, 
  onTransactionsSelect,
  disabled,
  allowMultiple,
  disputeType
}) {
  const [expanded, setExpanded] = useState(true);
  const [merchantSearch, setMerchantSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [transactions, setTransactions] = useState([]);
  
  // Initialize selected transactions when component loads
  useEffect(() => {
    if (selectedTransactions?.length > 0) {
      setSelectedIds(selectedTransactions.map(t => t.id));
      setExpanded(false);
    } else {
      setSelectedIds([]);
    }
  }, [selectedTransactions]);

  // Load transactions data
  useEffect(() => {
    const loadTransactions = async () => {
      try {
        const allTransactions = await Transaction.list();
        setTransactions(allTransactions);
      } catch (error) {
        console.error("Error loading transactions:", error);
      }
    };
    
    loadTransactions();
  }, []);

  const searchTransactions = () => {
    setIsSearching(true);
    
    try {
      let filtered = [...transactions];
      
      if (merchantSearch) {
        filtered = filtered.filter(t => 
          t.merchant.toLowerCase().includes(merchantSearch.toLowerCase())
        );
      }
      
      if (startDate) {
        filtered = filtered.filter(t => new Date(t.date) >= new Date(startDate));
      }
      
      if (endDate) {
        filtered = filtered.filter(t => new Date(t.date) <= new Date(endDate));
      }
      
      setFilteredTransactions(filtered);
      setHasSearched(true);
    } catch (error) {
      console.error("Error filtering transactions:", error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      searchTransactions();
    }
  };

  const handleCheckTransaction = (id) => {
    if (selectedIds.includes(id)) {
      // Remove if already selected
      setSelectedIds(selectedIds.filter(selectedId => selectedId !== id));
    } else {
      // Add if not already selected, respecting allowMultiple
      if (!allowMultiple && selectedIds.length > 0) {
        // If not allowing multiple, replace current selection
        setSelectedIds([id]);
      } else {
        // Add to existing selection
        setSelectedIds([...selectedIds, id]);
      }
    }
  };

  const applySelectedTransactions = () => {
    const selected = transactions.filter(t => selectedIds.includes(t.id));
    onTransactionsSelect(selected);
    setExpanded(false);
  };

  const clearSelectedTransactions = () => {
    onTransactionsSelect([]);
    setSelectedIds([]);
    setExpanded(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    return status === "Pending" ? (
      <Badge variant="outline" className="bg-yellow-50 text-yellow-800 border-yellow-200">
        Pending
      </Badge>
    ) : (
      <Badge variant="outline" className="bg-green-50 text-green-800 border-green-200">
        Settled
      </Badge>
    );
  };

  const getNinetyDayWarning = (date) => {
    const transactionDate = new Date(date);
    const today = new Date();
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(today.getDate() - 90);
    
    return transactionDate < ninetyDaysAgo;
  };

  const handleDateSelect = (type, date) => {
    if (type === 'start') {
      setStartDate(date ? format(date, "yyyy-MM-dd") : "");
    } else {
      setEndDate(date ? format(date, "yyyy-MM-dd") : "");
    }
  };

  return (
    <Card className="mb-6 border-l-4 border-l-purple-500 shadow-md">
      <CardHeader className="flex flex-row items-center justify-between py-4">
        <CardTitle 
          className="text-lg font-semibold flex items-center cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          <ShoppingCart className="mr-2 h-5 w-5 text-purple-500" />
          Transaction Selection {!allowMultiple && disputeType && <span className="text-sm ml-2 text-gray-500">(Select 1 transaction)</span>}
          {allowMultiple && disputeType && <span className="text-sm ml-2 text-gray-500">(Select multiple transactions)</span>}
          {expanded ? 
            <ChevronUp className="ml-2 h-4 w-4" /> : 
            <ChevronDown className="ml-2 h-4 w-4" />
          }
        </CardTitle>
        {selectedTransactions?.length > 0 && (
          <div className="flex items-center">
            <span className="text-sm bg-green-100 text-green-800 py-1 px-3 rounded-full flex items-center">
              <Check className="mr-1 h-3 w-3" />
              {selectedTransactions.length} Transaction{selectedTransactions.length !== 1 ? "s" : ""} Selected
            </span>
          </div>
        )}
      </CardHeader>
      
      <AnimatePresence>
        {expanded && selectedCardholder && selectedCard && !disabled ? (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <CardContent className="space-y-4 pb-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="relative">
                  <Input
                    placeholder="Search by merchant name..."
                    value={merchantSearch}
                    onChange={(e) => setMerchantSearch(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="pl-10"
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                </div>
                
                <div className="flex items-center space-x-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline"
                        className="w-full justify-start text-left font-normal flex items-center"
                      >
                        <Calendar className="mr-2 h-4 w-4 text-gray-400" />
                        {startDate ? format(new Date(startDate), "MMM d, yyyy") : "Start date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <CalendarComponent
                        mode="single"
                        selected={startDate ? new Date(startDate) : undefined}
                        onSelect={(date) => handleDateSelect('start', date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline"
                        className="w-full justify-start text-left font-normal flex items-center"
                      >
                        <Calendar className="mr-2 h-4 w-4 text-gray-400" />
                        {endDate ? format(new Date(endDate), "MMM d, yyyy") : "End date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <CalendarComponent
                        mode="single"
                        selected={endDate ? new Date(endDate) : undefined}
                        onSelect={(date) => handleDateSelect('end', date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              
              <div className="flex justify-end">
                <Button 
                  onClick={searchTransactions} 
                  disabled={isSearching}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {isSearching ? "Searching..." : "Search Transactions"}
                </Button>
              </div>
              
              {hasSearched && filteredTransactions.length === 0 && (
                <div className="text-center p-6 border rounded-md bg-gray-50">
                  <div className="text-gray-500">No transactions found matching your search criteria.</div>
                </div>
              )}
              
              {filteredTransactions.length > 0 && (
                <div className="border rounded-md overflow-hidden">
                  <div className="max-h-[400px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="w-[50px]">Select</TableHead>
                          <TableHead>Merchant</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Type</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredTransactions.map((transaction) => {
                          const isExpired = getNinetyDayWarning(transaction.date);
                          
                          return (
                            <TableRow 
                              key={transaction.id}
                              className={isExpired ? "bg-red-50" : ""}
                            >
                              <TableCell>
                                <Checkbox
                                  checked={selectedIds.includes(transaction.id)}
                                  onCheckedChange={() => handleCheckTransaction(transaction.id)}
                                  disabled={isExpired || (!allowMultiple && selectedIds.length > 0 && !selectedIds.includes(transaction.id))}
                                />
                              </TableCell>
                              <TableCell className="font-medium">{transaction.merchant}</TableCell>
                              <TableCell>{formatCurrency(transaction.amount)}</TableCell>
                              <TableCell>
                                {format(new Date(transaction.date), "MMM d, yyyy")}
                              </TableCell>
                              <TableCell>{getStatusBadge(transaction.settlement_status)}</TableCell>
                              <TableCell>
                                <Badge variant="outline" className="bg-gray-100">
                                  {transaction.type}
                                </Badge>
                              </TableCell>
                              {(isExpired || transaction.settlement_status === "Pending") && (
                                <TableCell>
                                  {isExpired ? (
                                    <div className="flex items-center text-red-600 text-xs">
                                      <AlertCircle className="h-3 w-3 mr-1" />
                                      Expired (90+ days)
                                    </div>
                                  ) : (
                                    <div className="flex items-center text-yellow-600 text-xs">
                                      <AlertCircle className="h-3 w-3 mr-1" />
                                      Transaction pending
                                    </div>
                                  )}
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                  
                  <div className="p-3 bg-gray-50 border-t flex items-center justify-between">
                    <div className="text-sm text-gray-500">
                      {selectedIds.length} of {filteredTransactions.length} transactions selected
                      {!allowMultiple && selectedIds.length > 0 && (
                        <span className="ml-2 text-purple-600">
                          (Only one transaction allowed for this dispute type)
                        </span>
                      )}
                    </div>
                    <Button 
                      onClick={applySelectedTransactions} 
                      disabled={selectedIds.length === 0}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      Apply Selection
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </motion.div>
        ) : (
          <CardContent>
            <div className="text-center p-4 text-gray-500">
              {!selectedCardholder 
                ? "Please select a cardholder first"
                : !selectedCard 
                  ? "Please select a card first"
                  : disabled
                    ? "Please select a dispute reason first"
                    : null
              }
            </div>
          </CardContent>
        )}
      </AnimatePresence>
    </Card>
  );
}
